import { Component, HostListener, OnInit } from '@angular/core';
import {CompetitionGoalModel, CompetitionModel} from '../../../../models/competition/competition.model';
import {AdminCompetitionService} from '../../../../services/admin/admin-competition.service';
import {ActivatedRoute, Params} from '@angular/router';
import {Enums} from '../../../../models/extensions/enums';
import {CultureModel} from '../../../../models/localization-and-pricing/culture.model';
import {ErrorModel} from '../../../../models/shared/error.model';
import {ModalService} from '../../../../shared/modals/modal/modal.service';
import {AdminIntegrationService} from '../../../../services/admin/admin-integration.service';
import {
  CompetitionAwardTypes,
  IntegrationAwardModel,
  IntegrationCompetitionAwardModel,
  IntegrationFilter,
  IntegrationTypes,
  ObstacleCourseLevels,
  QRCodeContentTypes,
  RewardGrantTypes
} from '../../../../models/qrcode-integrations/integration.model';
import {DropdownModel} from '../../../../models/component/dropdown.model';
import {EggRewardModel, ItemRewardModel, MonsterRewardModel, RewardModel, RewardTypes} from '../../../../models/qrcode-integrations/reward.model';

export class GoalRow {
  goal: CompetitionGoalModel;
  number = 0;
  status = 'upcoming';
  progressPct = 0;
  description = '';
  award: IntegrationAwardModel = null;
  awardChip = '';
  numMoreRewards = 0;
}

@Component({
  selector: 'app-admin-competitions-goals',
  templateUrl: './admin-competitions-goals.component.html',
  styleUrls: ['./admin-competitions-goals.component.css']
})
export class AdminCompetitionsGoalsComponent implements OnInit {

  comp: CompetitionModel;
  goals: CompetitionGoalModel[];

  showCreateGoal = false;
  editingGoalId: string = null;

  goal = new CompetitionGoalModel();
  award: IntegrationCompetitionAwardModel = null;
  CompetitionGoalTypes = Enums.CompetitionGoalTypes;
  CultureModel = CultureModel;
  integrationContentTypes = DropdownModel.generateFromKeys(QRCodeContentTypes.list());
  integrationRewardTypes = DropdownModel.generateFromKeys(RewardTypes.list());
  integrationRewardType = RewardTypes.item;
  integrationAwardTypes = DropdownModel.generateFromKeys(CompetitionAwardTypes.list());
  integrationGrantTypes = DropdownModel.generateFromKeys(RewardGrantTypes.list());
  integrationObstacleLevels = DropdownModel.generateFromKeys(ObstacleCourseLevels.list());

  // Overview
  rows: GoalRow[] = [];
  awardsById = {};
  totalAnswersLabel = '0';
  numReached = 0;
  progressText = '';
  openMenuGoalId: string = null;

  constructor(
    private adminCompSv: AdminCompetitionService,
    private adminIntSv: AdminIntegrationService,
    private route: ActivatedRoute,
    private modalSv: ModalService
  ) { }

  ngOnInit() {

    this.route.parent.params.subscribe((params: Params) => {
      const compId = params.competitionId;
      this.fetchCompetition(compId);
    });

  }

  fetchCompetition(compId: string) {

    if (compId) {

      this.adminCompSv.fetchCompetitionById(compId).subscribe((comp) => {
        this.setCompetition(comp);
      });

    }

  }

  // Overview

  /**
   * Consumer goals are reached per child by numCorrect >= endValue, so they have no start value,
   * completion or collective progress.
   */
  isConsumer(): boolean {
    return this.comp && this.comp.isConsumer();
  }

  getEndValueTitle(): string {

    if (this.isConsumer()) {
      return 'Correct answers to reach';
    }

    return 'End Value';

  }

  setCompetition(comp: CompetitionModel) {
    this.comp = comp;
    this.goals = comp.goals;
    this.rebuildRows();
    this.loadAwards();
  }

  /**
   * The awards of the competition, one request for all goals. The overview shows them next to their goal.
   */
  loadAwards() {

    if (!this.comp) {
      return;
    }

    const filter = new IntegrationFilter();
    filter.skip = 0;
    filter.limit = 100;
    filter.integrationType = IntegrationTypes.competitionAward;
    filter.competitionId = this.comp._id;

    this.adminIntSv.fetchIntegrations(filter).subscribe((integrations) => {

      const awardsById = {};
      for (const integration of integrations) {
        awardsById[integration._id] = integration;
      }

      this.awardsById = awardsById;
      this.rebuildRows();

    });

  }

  rebuildRows() {

    const rows: GoalRow[] = [];
    const numAnswers = this.comp.numAnswers;

    for (let i = 0; i < this.goals.length; i++) {

      const goal = this.goals[i];
      const row = new GoalRow();
      row.goal = goal;
      row.number = i + 1;
      row.description = this.getGoalText(goal);

      if (goal.completed) {
        row.status = 'reached';
        row.progressPct = 100;
      } else if (numAnswers >= goal.startValue) {
        row.status = 'progress';
        row.progressPct = this.getProgressPct(numAnswers, goal);
      }

      this.setRowAward(row);
      rows.push(row);

    }

    this.rows = rows;
    this.totalAnswersLabel = this.formatNumber(numAnswers);
    this.setSummary();

  }

  getGoalText(goal: CompetitionGoalModel): string {

    const description = goal.translatedDescription['en-GB'];
    if (description) {
      return description;
    }

    const name = goal.translatedName['en-GB'];
    if (name) {
      return name;
    }

    return '';

  }

  getProgressPct(numAnswers: number, goal: CompetitionGoalModel): number {

    const size = goal.endValue - goal.startValue;
    if (size <= 0) {
      return 0;
    }

    const pct = ((numAnswers - goal.startValue) / size) * 100;
    return Math.max(0, Math.min(100, pct));

  }

  setRowAward(row: GoalRow) {

    if (!row.goal.integrationId) {
      return;
    }

    const award = this.awardsById[row.goal.integrationId] as IntegrationAwardModel;
    if (!award) {
      return;
    }

    row.award = award;

    if (award.rewards.length > 0) {
      row.awardChip = award.rewards[0].name;
      row.numMoreRewards = award.rewards.length - 1;
    } else if (award.awardType === CompetitionAwardTypes.obstacleCourse) {
      row.awardChip = 'Obstacle course';
    } else {
      row.awardChip = 'No rewards';
    }

  }

  setSummary() {

    let numReached = 0;
    let progressRow: GoalRow = null;

    for (const row of this.rows) {

      if (row.status === 'reached') {
        numReached += 1;
      } else if (row.status === 'progress' && !progressRow) {
        progressRow = row;
      }

    }

    this.numReached = numReached;
    this.progressText = '';

    if (progressRow) {

      const goal = progressRow.goal;
      const done = this.comp.numAnswers - goal.startValue;
      const size = goal.endValue - goal.startValue;
      this.progressText = `${this.formatNumber(done)} of ${this.formatNumber(size)} (${Math.round(progressRow.progressPct)}%)`;

    }

  }

  getProgressRowNumber(): number {

    for (const row of this.rows) {
      if (row.status === 'progress') {
        return row.number;
      }
    }

    return 0;

  }

  formatNumber(value: number): string {

    if (value === null || value === undefined) {
      return '0';
    }

    return Number(value).toLocaleString('en-GB');

  }

  getStatusLabel(row: GoalRow): string {

    if (row.status === 'reached') {
      return 'Reached';
    }

    if (row.status === 'progress') {
      return 'In progress';
    }

    return 'Upcoming';

  }

  isGoalRangeLocked(): boolean {
    return this.editingGoalId !== null && this.goal.completed === true;
  }

  // Menu of a goal row

  onToggleMenu(goal: CompetitionGoalModel, event: Event) {

    event.stopPropagation();

    if (this.openMenuGoalId === goal._id) {
      this.openMenuGoalId = null;
    } else {
      this.openMenuGoalId = goal._id;
    }

  }

  @HostListener('document:click')
  onDocumentClicked() {
    this.openMenuGoalId = null;
  }

  onShowCreateGoalClicked() {

    let lastGoal = null;
    if (this.goals.length > 0) {
      lastGoal = this.goals[this.goals.length - 1];
    }

    this.goal = new CompetitionGoalModel();
    this.goal.goalType = this.CompetitionGoalTypes.answers;

    if (lastGoal && !this.isConsumer()) {
      this.goal.startValue = lastGoal.endValue;
    }

    this.showCreateGoal = true;
  }

  onCreateGoal() {

    this.adminCompSv.addGoalToCompetition(this.comp._id, this.goal).subscribe((comp) => {
      this.modalSv.showAlertModal('Success', 'Goal was successfully created');
      this.setCompetition(comp);
      this.showCreateGoal = false;
      this.goal = new CompetitionGoalModel();
    }, (err: ErrorModel) => {
      this.modalSv.showTranslatedErrorModal(err);
    });

  }

  onEditClicked(goal: CompetitionGoalModel) {

    this.editingGoalId = goal._id;
    this.goal = CompetitionGoalModel.generateModel(goal);
    this.award = null;

    if (this.goal && this.goal.integrationId) {
      this.adminIntSv.fetchIntegrationById(goal.integrationId).subscribe((integration) => {
        this.award = integration as IntegrationCompetitionAwardModel;
      });
    }

    this.showCreateGoal = true;
  }

  onUpdateGoal() {

    this.adminCompSv.updateGoalInCompetition(this.comp._id, this.goal._id, this.goal).subscribe((comp) => {
      this.modalSv.showAlertModal('Success', 'Goal was successfully updated');
      this.setCompetition(comp);
      this.showCreateGoal = false;
      this.editingGoalId = null;
      this.goal = new CompetitionGoalModel();
    }, (err: ErrorModel) => {
      this.modalSv.showTranslatedErrorModal(err);
    });

  }

  onDeleteClicked(goal: CompetitionGoalModel) {

    this.modalSv.showChoiceModal(
      'Delete Goal',
      'Are you sure you want to delete this goal?',
      'Yes', 'No').subscribe(() => {
        this.adminCompSv.removeGoalFromCompetition(this.comp._id, goal._id).subscribe((comp) => {
          this.modalSv.showAlertModal('Success', 'Goal was successfully deleted');
          this.setCompetition(comp);
        }, (err: ErrorModel) => {
          this.modalSv.showTranslatedErrorModal(err);
        });
      }
    );

  }

  onSubmit() {

    // Drop image rows that were added but left empty
    this.goal.imgURLs = this.goal.imgURLs.filter((url) => url && url.trim().length > 0);

    if (this.editingGoalId) {
      this.onUpdateGoal();
    } else {
      this.onCreateGoal();
    }
  }

  onCreateClicked() {
    this.editingGoalId = null;
    this.goal = new CompetitionGoalModel();
    this.showCreateGoal = true;
  }

  onCancel() {
    this.showCreateGoal = false;
    this.editingGoalId = null;
    this.goal = new CompetitionGoalModel();
    this.award = null;
  }

  onCreateIntegrationClicked(goal: CompetitionGoalModel) {

    const integration = new IntegrationCompetitionAwardModel();
    integration.integrationType = IntegrationTypes.competitionAward;

    // The goal passed in can be an edited copy, so find it by id and not by reference.
    let goalNumber = 0;
    for (let i = 0; i < this.comp.goals.length; i++) {
      if (this.comp.goals[i]._id === goal._id) {
        goalNumber = i + 1;
      }
    }

    integration.name = `Goal ${goalNumber} award`;
    integration.numUsesPerStudent = 1;
    integration.competitionGoalId = goal._id;
    integration.competitionId = this.comp._id;

    this.adminIntSv.createOrUpdateIntegration(integration).subscribe((createdIntegration) => {
      this.goal.integrationId = createdIntegration._id;
      this.adminCompSv.updateGoalInCompetition(this.comp._id, this.goal._id, this.goal).subscribe((comp) => {

        // Keep the goals list in sync, it is what the edit button reads the goal from.
        this.setCompetition(comp);
        this.award = createdIntegration as IntegrationCompetitionAwardModel;

      }, (err: ErrorModel) => {

        // The award exists but is not linked to the goal yet.
        this.goal.integrationId = null;
        this.modalSv.showTranslatedErrorModal(err);

      });
    }, (err: ErrorModel) => {
      this.modalSv.showTranslatedErrorModal(err);
    });
  }

  /**
   * Deletes the award of the goal, which is only possible when no student has redeemed it.
   * The goal is unlinked from the award.
   */
  onDeleteAwardClicked() {

    this.modalSv.showChoiceModal(
      'Delete Award',
      'Are you sure you want to delete this award? The goal will no longer have an award.',
      'Yes', 'No').subscribe(() => {

        this.adminIntSv.deleteIntegration(this.award._id).subscribe(() => {

          this.award = null;
          this.goal.integrationId = null;
          this.fetchCompetition(this.comp._id);
          this.modalSv.showAlertModal('Success', 'Award was successfully deleted');

        }, (err: ErrorModel) => {
          this.modalSv.showTranslatedErrorModal(err);
        });

      }
    );

  }

  onAwardTypeChanged(awardType: string) {

    this.award.awardType = awardType;

    if (awardType === CompetitionAwardTypes.obstacleCourse) {

      if (this.award.obstacleCourseLevel === ObstacleCourseLevels.none) {
        this.award.obstacleCourseLevel = ObstacleCourseLevels.beach;
      }

    } else {
      this.award.obstacleCourseLevel = ObstacleCourseLevels.none;
    }

  }

  // The number inputs give strings
  prepareAward() {
    this.award.numUsesPerStudent = Number(this.award.numUsesPerStudent);
    this.award.numQuestions = Number(this.award.numQuestions);

    for (const reward of this.award.rewards) {
      if (reward.rewardType === RewardTypes.egg) {
        const eggReward = reward as EggRewardModel;
        eggReward.numRequired = Number(eggReward.numRequired);
      }
    }

  }

  onCreateReward() {

    let reward = new RewardModel();
    if (this.integrationRewardType === RewardTypes.item) {

      const itemReward = new ItemRewardModel();
      itemReward.name = 'Apple';
      itemReward.itemId = 'a5HSNRzN3aZR';
      itemReward.numItems = 3;
      reward = itemReward;

    } else if (this.integrationRewardType === RewardTypes.egg) {

      const eggReward = new EggRewardModel();
      eggReward.name = 'Egg';
      eggReward.itemId = '';
      eggReward.numRequired = 10;
      reward = eggReward;

    } else if (this.integrationRewardType === RewardTypes.monster) {

      const monsterReward = new MonsterRewardModel();
      monsterReward.name = 'Blither';
      monsterReward.monsterId = 'MQEs6IlJk6rp';
      monsterReward.familyId = '9PXHSePFBQys';
      monsterReward.skinId = null;
      monsterReward.xp = 10;
      monsterReward.abilityOneLevel = 1;
      monsterReward.abilityTwoLevel = 0;
      monsterReward.abilityThreeLevel = 0;
      monsterReward.abilityFourLevel = 0;
      reward = monsterReward;

    }

    this.award.rewards.push(reward);
    this.prepareAward();
    this.adminIntSv.createOrUpdateIntegration(this.award).subscribe((award) => {
      this.award = award as IntegrationCompetitionAwardModel;
    });

  }

  onUpdateIntegration() {

    this.prepareAward();
    this.adminIntSv.createOrUpdateIntegration(this.award).subscribe((award) => {

      this.award = award as IntegrationCompetitionAwardModel;
      this.loadAwards();
      this.modalSv.showAlertModal('Success', 'Integration updated.');

    }, (err: ErrorModel) => {
      this.modalSv.showTranslatedErrorModal(err);
    });

  }

  onRemoveIntegration(index: number) {

    this.award.rewards.splice(index, 1);
    this.prepareAward();
    this.adminIntSv.createOrUpdateIntegration(this.award).subscribe((award) => {

      this.award = award as IntegrationCompetitionAwardModel;
      this.modalSv.showAlertModal('Success', 'Integration removed');

    });

  }


  trackByIndex(index: number): number {
    return index;
  }

  addImgURL() {
    this.goal.imgURLs.push('');
  }

  removeImgURL(index: number) {
    this.goal.imgURLs.splice(index, 1);
  }

}
